/**
 * Preloads preference header images for smoother paint.
 *
 * Belongs here: image preload hook.
 * Does not belong here: questionnaire logic.
 */
import { useEffect, useMemo, useState } from 'react'
import { ImageAvailability } from '../shared/types/common.types'

const preloadedUrls = new Set<string>()
const availabilityCache = new Map<string, boolean>()

export function useExpertReviewPreloadImages(urls: string[]) {
  const normalizedUrls = useMemo(() => {
    const unique = new Set<string>()
    urls.forEach((url) => {
      const trimmed = url?.trim?.() ?? ''
      if (trimmed) unique.add(trimmed)
    })
    return Array.from(unique)
  }, [urls])

  useEffect(() => {
    normalizedUrls.forEach((url) => {
      if (preloadedUrls.has(url)) return
      preloadedUrls.add(url)
      try {
        const img = new Image()
        img.src = url
      } catch {
        // ignore
      }
    })
  }, [normalizedUrls])
}

export function useExpertReviewImageAvailabilityByUrl(urls: string[]): Record<string, ImageAvailability> {
  const normalizedUrls = useMemo(() => {
    const unique = new Set<string>()
    urls.forEach((url) => {
      const trimmed = url?.trim?.() ?? ''
      if (trimmed) unique.add(trimmed)
    })
    return Array.from(unique)
  }, [urls])

  const [availabilityByUrl, setAvailabilityByUrl] = useState<Record<string, ImageAvailability>>({})

  useEffect(() => {
    let isCancelled = false

    normalizedUrls.forEach((url) => {
      const cached = availabilityCache.get(url)
      if (cached === true) {
        setAvailabilityByUrl((prev) =>
          prev[url] === ImageAvailability.AVAILABLE ? prev : { ...prev, [url]: ImageAvailability.AVAILABLE }
        )
        return
      }
      if (cached === false) {
        setAvailabilityByUrl((prev) =>
          prev[url] === ImageAvailability.UNAVAILABLE ? prev : { ...prev, [url]: ImageAvailability.UNAVAILABLE }
        )
        return
      }

      if (preloadedUrls.has(url)) {
        setAvailabilityByUrl((prev) =>
          prev[url] ? prev : { ...prev, [url]: ImageAvailability.UNKNOWN }
        )
        return
      }

      preloadedUrls.add(url)
      try {
        const img = new Image()
        img.onload = () => {
          if (isCancelled) return
          availabilityCache.set(url, true)
          setAvailabilityByUrl((prev) => ({ ...prev, [url]: ImageAvailability.AVAILABLE }))
        }
        img.onerror = () => {
          if (isCancelled) return
          availabilityCache.set(url, false)
          setAvailabilityByUrl((prev) => ({ ...prev, [url]: ImageAvailability.UNAVAILABLE }))
        }
        img.src = url
        setAvailabilityByUrl((prev) =>
          prev[url] ? prev : { ...prev, [url]: ImageAvailability.UNKNOWN }
        )
      } catch {
        availabilityCache.set(url, false)
        setAvailabilityByUrl((prev) => ({ ...prev, [url]: ImageAvailability.UNAVAILABLE }))
      }
    })

    return () => {
      isCancelled = true
    }
  }, [normalizedUrls])

  return availabilityByUrl
}
